import React from "react";
import {
  Banknote,
  BedDouble,
  Building2,
  Car,
  Coffee,
  Home,
  MapPin,
  Save,
  Settings2,
  ShieldCheck,
  Truck,
  Loader2,
} from "lucide-react";

import { BORDER_SMART, THEME_BLUE_BG } from "./configHelpers";

import { Card, Field, FormSection, Input, Toggle } from "./ClientConfigShared";

function CompactCapabilityBox({ active, label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-[28px] items-center gap-1.5 rounded-md border px-1.5 text-left transition-all ${
        active
          ? "border-[#01C37D] bg-[#01C37D]/10 text-[#007e55]"
          : `${BORDER_SMART} bg-white text-gray-600 hover:border-[#01C37D]/60`
      }`}
    >
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${
          active ? "bg-[#01C37D] text-white" : "bg-gray-100 text-gray-500"
        }`}
      >
        <Icon size={10} />
      </span>

      <span className="truncate text-[10px] font-bold leading-none">
        {label}
      </span>
    </button>
  );
}

export default function ConfigFormCard({
  formData,
  updateForm,
  onSave,
  configLoading,
  isSavingConfig,
}) {
  return (
    <Card
      title="Configuration Form"
      icon={Settings2}
      rightAction={
        <div className="hidden items-center gap-1 rounded-full bg-gray-50 px-1.5 py-0.5 text-[9px] font-semibold text-gray-500 md:inline-flex">
          <ShieldCheck size={10} />
          GST protected
        </div>
      }
    >
      <div className="space-y-1.5">
        <FormSection title="Business Profile" icon={Building2}>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-[1fr_160px_1fr]">
            <Field label="Restaurant Name">
              <Input
                value={formData.restaurantName}
                onChange={(e) => updateForm("restaurantName", e.target.value)}
              />
            </Field>

            <Field label="Mobile Number">
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="10 digit mobile"
                value={formData.mobileNumber || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);

                  updateForm("mobileNumber", value);
                }}
              />
            </Field>

            <div className="flex min-h-[32px] items-center rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[9px] font-medium leading-3 text-amber-700">
              GST Number is managed from GST Details card.
            </div>
          </div>
        </FormSection>

        <FormSection title="Service Capabilities" icon={Truck}>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-5">
            {[
              { field: "homeDelivery", label: "Delivery", icon: Truck },
              { field: "takeaway", label: "Take Away", icon: Coffee },
              { field: "dining", label: "Dining", icon: Home },
              { field: "carOrder", label: "Car Order", icon: Car },
              { field: "roomOrder", label: "Room Order", icon: BedDouble },
            ].map((item) => (
              <CompactCapabilityBox
                key={item.field}
                active={formData[item.field]}
                label={item.label}
                icon={item.icon}
                onClick={() => updateForm(item.field, !formData[item.field])}
              />
            ))}
          </div>
        </FormSection>

        <FormSection title="Fulfillment Address" icon={MapPin}>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-[1fr_115px]">
            <Field label="Physical Address">
              <textarea
                className={`min-h-[34px] w-full resize-none rounded-md border ${BORDER_SMART} bg-white px-2 py-1 text-[11px] text-gray-800 outline-none transition-all placeholder:text-gray-400 focus:border-[#01C37D] focus:ring-1 focus:ring-[#01C37D]/10`}
                placeholder="Enter complete business address"
                value={formData.restaurantAddress}
                onChange={(e) =>
                  updateForm("restaurantAddress", e.target.value)
                }
              />
            </Field>

            <Field label="Pincode">
              <Input
                value={formData.restaurantPincode}
                onChange={(e) =>
                  updateForm("restaurantPincode", e.target.value)
                }
              />
            </Field>
          </div>
        </FormSection>

        <FormSection title="POS & Settlement" icon={Banknote}>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-3">
            <div
              className={`rounded-md border ${BORDER_SMART} ${THEME_BLUE_BG} px-2 py-1`}
            >
              <div className="flex min-h-[30px] items-center justify-between gap-2">
                <div>
                  <p className="text-[10px] font-bold leading-none text-gray-900">
                    POS Enabled
                  </p>
                  <p className="mt-0.5 text-[9px] font-medium leading-none text-gray-500">
                    Allow POS orders
                  </p>
                </div>

                <Toggle
                  checked={formData.posOrder}
                  onChange={(value) => updateForm("posOrder", value)}
                />
              </div>
            </div>

            <Field label="Settlement Account">
              <Input
                type="number"
                value={formData.settlementAccount}
                onChange={(e) =>
                  updateForm("settlementAccount", e.target.value)
                }
              />
            </Field>

            <Field label="Settlement IFSC">
              <Input
                className="uppercase"
                value={formData.settlementAccountifsc}
                onChange={(e) =>
                  updateForm(
                    "settlementAccountifsc",
                    e.target.value.toUpperCase(),
                  )
                }
              />
            </Field>
          </div>
        </FormSection>

        <div
          className={`flex flex-col gap-1.5 rounded-lg border ${BORDER_SMART} bg-white/80 px-2 py-1.5 md:flex-row md:items-center md:justify-between`}
        >
          <div>
            <p className="text-[10px] font-semibold leading-none text-gray-900">
              Review all details
            </p>
            <p className="mt-0.5 text-[9px] leading-none text-gray-500">
              Changes will reflect instantly.
            </p>
          </div>

          <button
            type="button"
            onClick={onSave}
            disabled={configLoading || isSavingConfig}
            className="flex w-full items-center justify-center gap-1 rounded-md bg-[#01C37D] px-3 py-1.5 text-[10px] font-bold text-white shadow-sm shadow-[#01C37D]/20 transition-all hover:bg-[#00ab6d] active:scale-95 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none md:w-auto"
          >
            {isSavingConfig ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Save size={11} />
            )}
            {isSavingConfig ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </Card>
  );
}
